import { useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import { alpha } from "@mui/material/styles";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Validation
import validateEmployee from "../schemas/validations";

function EmployeeUpdateForm({ formData, handleChange, handleBlur, departments, positions, managers, degrees, contractTypes, natureOfWorkOptions, errors }) {
  const [managerInputValue, setManagerInputValue] = useState('');

  // الحصول على اسم المدير المحدد لعرضه في AutoComplete
  const getSelectedManager = () => {
    if (!formData.managerId) return null;
    const selectedManager = managers.find(m => m.id === formData.managerId);
    return selectedManager || null;
  };

  return (
    <MDBox>
      <MDBox display="flex" alignItems="center" mb={3}>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="3.5rem"
          height="3.5rem"
          variant="gradient"
          bgColor="info"
          color="white"
          shadow="md"
          borderRadius="xl"
          mr={2}
        >
          <PersonAddIcon sx={{ fontSize: 28 }} />
        </MDBox>
        <MDBox>
          <MDTypography variant="h3" fontWeight="medium" color="info" mb={0.5}>
            تعديل بيانات الموظف / Update Employee
          </MDTypography>
          <MDTypography variant="body2" color="text" opacity={0.8}>
            قم بتعديل بيانات الموظف / Update employee details
          </MDTypography>
        </MDBox>
      </MDBox>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* First Row */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.name}>
            <MDInput
              type="text"
              label="الاسم / Name"
              value={formData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              onBlur={() => handleBlur('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.email}>
            <MDInput
              type="email"
              label="البريد الإلكتروني / Email"
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.phone}>
            <MDInput
              type="tel"
              label="رقم الهاتف / Phone Number"
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              onBlur={() => handleBlur('phone')}
              error={!!errors.phone}
              helperText={errors.phone}
              required
              inputProps={{
                pattern: "[0-9]*",
                maxLength: 11
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                },
              }}
            />
          </FormControl>
        </Grid>

        {/* Second Row */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.departmentId}>
            <InputLabel required>القسم / Department</InputLabel>
            <Select
              value={formData.departmentId || ''}
              onChange={(e) => handleChange('departmentId', e.target.value)}
              onBlur={() => handleBlur('departmentId')}
              label="القسم / Department"
              error={!!errors.departmentId}
              sx={{
                height: "42px",
                "&:hover fieldset": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
              }}
            >
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
            {errors.departmentId && (
              <FormHelperText error>{errors.departmentId}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.positionId}>
            <InputLabel required>المنصب / Position</InputLabel>
            <Select
              value={formData.positionId || ''}
              onChange={(e) => handleChange('positionId', e.target.value)}
              onBlur={() => handleBlur('positionId')}
              label="المنصب / Position"
              error={!!errors.positionId}
              sx={{
                height: "42px",
                "&:hover fieldset": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
              }}
            >
              {positions.map((pos) => (
                <MenuItem key={pos.id} value={pos.id}>
                  {pos.name}
                </MenuItem>
              ))}
            </Select>
            {errors.positionId && (
              <FormHelperText error>{errors.positionId}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
        <FormControl fullWidth error={!!errors.managerId}>
          <Autocomplete
            options={managers}
            getOptionLabel={(option) => option.name}
            value={getSelectedManager()}
            onChange={(event, newValue) => {
              handleChange("managerId", newValue ? newValue.id : null);
            }}
            inputValue={managerInputValue}
            onInputChange={(event, newInputValue) => {
              setManagerInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="المدير / Manager *"
                error={!!errors.managerId}
                helperText={errors.managerId}
                onBlur={() => handleBlur("managerId")}
                required
              />
            )}
            noOptionsText="لا توجد نتائج"
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
                "& fieldset": {
                  borderColor: errors.managerId ? '#d32f2f' : undefined,
                }
              },
            }}
          />
        </FormControl>
      </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.dob}>
            <MDInput
              type="date"
              label="تاريخ الميلاد / Date of Birth"
              value={formData.dob || ''}
              onChange={(e) => handleChange('dob', e.target.value)}
              onBlur={() => handleBlur('dob')}
              error={!!errors.dob}
              helperText={errors.dob}
              required
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: errors.dob ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
          </FormControl>
        </Grid>

        {/* Third Row */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.joinDate}>
            <MDInput
              type="date"
              label="تاريخ الإلتحاق / Join Date"
              value={formData.joinDate || ''}
              onChange={(e) => handleChange('joinDate', e.target.value)}
              onBlur={() => handleBlur('joinDate')}
              error={!!errors.joinDate}
              helperText={errors.joinDate}
              required
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                  "& fieldset": {
                    borderColor: errors.joinDate ? '#d32f2f' : undefined,
                  }
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.natureOfWork}>
            <InputLabel required>طبيعة العمل / Nature of Work</InputLabel>
            {natureOfWorkOptions.forEach(option => {
         
              if (option.name === formData.natureOfWork) {
                formData.natureOfWork = option.id;
              }
            })}
            <Select
              value={formData.natureOfWork || 0}
              onChange={(e) => handleChange('natureOfWork', parseInt(e.target.value))}
              onBlur={() => handleBlur('natureOfWork')}
              label="طبيعة العمل / Nature of Work"
              error={!!errors.natureOfWork}
              sx={{
                height: "42px",
                "&:hover fieldset": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
                "& fieldset": {
                  borderColor: errors.natureOfWork ? '#d32f2f' : undefined,
                }
              }}
            >
              <MenuItem value={0}>
                <em>اختر طبيعة العمل / Select Nature of Work</em>
              </MenuItem>
              {natureOfWorkOptions && natureOfWorkOptions.map((option) => (
                <MenuItem key={option.id} value={parseInt(option.id)}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
            {errors.natureOfWork && (
              <FormHelperText error>{errors.natureOfWork}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Fourth Row */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.contractType}>
            <InputLabel required>نوع العقد / Contract Type</InputLabel>
            <Select
              value={formData.contractType || ''}
              onChange={(e) => handleChange('contractType', e.target.value)}
              onBlur={() => handleBlur('contractType')}
              label="نوع العقد / Contract Type"
              error={!!errors.contractType}
              sx={{
                height: "42px",
                "&:hover fieldset": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
              }}
            >
              <MenuItem value="">
                <em>اختر نوع العقد / Select Contract Type</em>
              </MenuItem>
              {contractTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            {errors.contractType && (
              <FormHelperText error>{errors.contractType}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.degree}>
            <InputLabel required>الدرجة العلمية / Degree</InputLabel>
            <Select
              value={formData.degree || ''}
              onChange={(e) => handleChange('degree', e.target.value)}
              onBlur={() => handleBlur('degree')}
              label="الدرجة العلمية / Degree"
              error={!!errors.degree}
              sx={{
                height: "42px",
                "&:hover fieldset": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
              }}
            >
              <MenuItem value="">
                <em>اختر الدرجة العلمية / Select Degree</em>
              </MenuItem>
              {degrees.map((degree) => (
                <MenuItem key={degree.id} value={degree.id}>
                  {degree.name}
                </MenuItem>
              ))}
            </Select>
            {errors.degree && (
              <FormHelperText error>{errors.degree}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.major}>
            <MDInput
              type="text"
              label="التخصص / Major"
              value={formData.major || ''}
              onChange={(e) => handleChange('major', e.target.value)}
              onBlur={() => handleBlur('major')}
              error={!!errors.major}
              helperText={errors.major}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                },
              }}
            />
          </FormControl>
        </Grid>

        {/* Fifth Row */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.jobTitle}>
            <MDInput
              type="text"
              label="المسمى الوظيفي / Job Title"
              value={formData.jobTitle || ''}
              onChange={(e) => handleChange('jobTitle', e.target.value)}
              onBlur={() => handleBlur('jobTitle')}
              error={!!errors.jobTitle}
              helperText={errors.jobTitle}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.badgeNumber}>
            <MDInput
              type="text"
              label="رقم الشارة / Badge Number"
              value={formData.badgeNumber || ''}
              onChange={(e) => handleChange('badgeNumber', e.target.value)}
              onBlur={() => handleBlur('badgeNumber')}
              error={!!errors.badgeNumber}
              helperText={errors.badgeNumber}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.fingerPrintId}>
            <MDInput
              type="text"
              label="رقم البصمة / Fingerprint ID"
              value={formData.fingerPrintId || ''}
              onChange={(e) => handleChange('fingerPrintId', e.target.value)}
              onBlur={() => handleBlur('fingerPrintId')}
              error={!!errors.fingerPrintId}
              helperText={errors.fingerPrintId}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px",
                  "&:hover fieldset": {
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                  },
                },
              }}
            />
          </FormControl>
        </Grid>

        {/* Sixth Row */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.gender}>
            <InputLabel required>الجنس / Gender</InputLabel>
            <Select
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              onBlur={() => handleBlur('gender')}
              label="الجنس / Gender"
              error={!!errors.gender}
              sx={{
                height: "42px",
                "&:hover fieldset": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
              }}
            >
              <MenuItem value="">
                <em>اختر الجنس / Select Gender</em>
              </MenuItem>
              <MenuItem value={true}>ذكر / Male</MenuItem>
              <MenuItem value={false}>أنثى / Female</MenuItem>
            </Select>
            {errors.gender && (
              <FormHelperText error>{errors.gender}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth error={!!errors.isResigned}>
            <InputLabel required>حالة الموظف / Employee Status</InputLabel>
            <Select
              value={formData.isResigned}
              onChange={(e) => handleChange('isResigned', e.target.value)}
              onBlur={() => handleBlur('isResigned')}
              label="حالة الموظف / Employee Status"
              error={!!errors.isResigned}
              sx={{
                height: "42px",
                "&:hover fieldset": {
                  borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                },
              }}
            >
              <MenuItem value="">
                <em>اختر الحالة / Select Status</em>
              </MenuItem>
              <MenuItem value={false}>مستمر بالخدمة/ Active</MenuItem>
              <MenuItem value={true}>مستقيل / Resigned</MenuItem>
            </Select>
            {errors.isResigned && (
              <FormHelperText error>{errors.isResigned}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </MDBox>
  );
}

EmployeeUpdateForm.propTypes = {
  formData: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    positionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    managerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    degree: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dob: PropTypes.string,
    joinDate: PropTypes.string,
    natureOfWork: PropTypes.number,
    contractType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    major: PropTypes.string,
    jobTitle: PropTypes.string,
    badgeNumber: PropTypes.string,
    fingerPrintId: PropTypes.string,
    gender: PropTypes.bool,
    isResigned: PropTypes.bool
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  departments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  positions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  managers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  degrees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  contractTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  natureOfWorkOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  errors: PropTypes.object
};

export default EmployeeUpdateForm;
